import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { captureAttribution } from "@/lib/attribution";

const LANDING_PAGE_TITLE = "South Bay Puppy Training | Positive Puppy Training School";
const LANDING_PAGE_DESCRIPTION = "South Bay puppy training in Hermosa Beach for puppies 10 weeks to 10 months old. Positive training for potty, crate, leash, socialization, and more.";
const MainLandingPage = lazy(() => import("@/pages/MainLandingPage"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const PottyTrainingPage = lazy(() => import("@/pages/PottyTrainingPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const FourteenDayPage = lazy(() => import("@/pages/FourteenDayPage"));

function DeferredRoute({ children }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

function App() {
  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const isThankYouPage = currentPath === "/thank-you";
  const isLandingPage = currentPath === "/";
  const isPottyTrainingPage = currentPath === "/potty-training";
  const isCrateTrainingPage = currentPath === "/crate-training";
  const isBitingPage = currentPath === "/biting";
  const isBookingPage = currentPath === "/booking";
  const isFourteenDayPage = currentPath === "/14-day";

  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    document.title = isThankYouPage
      ? "Thank You | Puppyhood Made Easy"
      : isPottyTrainingPage
        ? "Potty Training | Puppyhood Made Easy"
        : isCrateTrainingPage
          ? "Crate Training | Puppyhood Made Easy"
          : isBitingPage
            ? "Puppy Biting | Puppyhood Made Easy"
            : isBookingPage
              ? "Request Your Consultation | Puppyhood Made Easy"
              : isFourteenDayPage
                ? "Free 14-Day Puppy Training Trial | The Puppy Academy"
                : isLandingPage
                  ? LANDING_PAGE_TITLE
                  : "Page Not Found | Puppyhood Made Easy";
    const description = document.querySelector('meta[name="description"]');
    if (description && isLandingPage) description.content = LANDING_PAGE_DESCRIPTION;
  }, [isBitingPage, isBookingPage, isCrateTrainingPage, isFourteenDayPage, isLandingPage, isPottyTrainingPage, isThankYouPage]);

  if (isThankYouPage) return <DeferredRoute><ThankYouPage /></DeferredRoute>;
  if (isPottyTrainingPage) return <DeferredRoute><PottyTrainingPage /></DeferredRoute>;
  if (isCrateTrainingPage) return <DeferredRoute><PottyTrainingPage variant="crate" heroHeadline="Crate Train Your Puppy Fast & Easy" heroImage="https://customer-assets-39nsmqrw.emergentagent.net/job_layout-port-studio/artifacts/31d2c647bc9292ac_ayla-verschueren-qvbG3-tZnyc-unsplash.webp" heroImageLabel="Puppy resting inside an open crate" /></DeferredRoute>;
  if (isBitingPage) return <DeferredRoute><PottyTrainingPage variant="biting" heroHeadline="Stop Puppy Biting Fast & Easy" heroImage="https://customer-assets-39nsmqrw.emergentagent.net/job_layout-port-studio/artifacts/6afde15c44a32c3c_image%20%289%29.png" heroImageLabel="Puppy gently biting a person's trouser leg outdoors" /></DeferredRoute>;
  if (isBookingPage) return <DeferredRoute><BookingPage /></DeferredRoute>;
  if (isFourteenDayPage) return <DeferredRoute><FourteenDayPage /></DeferredRoute>;
  if (!isLandingPage) return <DeferredRoute><NotFoundPage /></DeferredRoute>;

  return <DeferredRoute><MainLandingPage /></DeferredRoute>;
}

export default App;